import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, CheckCircle2, Circle, Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, money, logoUrl } from "@/lib/api";

const STEPS = [["received", "Request received", "We have your request and an analyst will confirm scope, price and timing."],
  ["in_progress", "Study in progress", "Your study is being prepared: site data, market research and financial projections."],
  ["ready", "Ready in your portal", "Your study is ready to review in your private client portal."]];

export default function StatusPage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => { api.get(`/public/status/${token}`).then((r) => setD(r.data)).catch(() => setError(true)); }, [token]);
  if (error) return <div className="min-h-screen grid place-items-center text-slate-400" data-testid="status-not-found">Request not found.</div>;
  if (!d) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-400" /></div>;
  const idx = STEPS.findIndex(([k]) => k === d.progress);
  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 py-12 px-4" data-testid="status-page">
      <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="p-8 border-b border-slate-200 flex items-center justify-between gap-4">
          {d.has_logo ? <img src={logoUrl("status")} alt={d.company_name} className="max-h-12 max-w-[180px] object-contain" /> : <span className="font-display text-xl font-bold">{d.company_name}</span>}
          <p className="text-xs uppercase tracking-wider text-slate-500">Request status</p>
        </div>
        <div className="p-8 space-y-6">
          <div>
            <p className="text-sm text-slate-500">Hi {d.name},</p>
            <h1 className="font-display text-2xl font-bold">{d.tier_label} feasibility study</h1>
            <p className="text-xs text-slate-500 mt-1">Requested {d.created_at.slice(0, 10)}{d.referral ? ` · Referred by ${d.referral.name}${d.referral.bank ? `, ${d.referral.bank}` : ""}` : ""}</p>
          </div>
          <ol className="space-y-5" data-testid="status-steps">
            {STEPS.map(([k, title, text], i) => {
              const done = i < idx || (i === idx && k === "ready"), now = i === idx && k !== "ready";
              const Icon = done ? CheckCircle2 : now ? Clock : Circle;
              return (
                <li key={k} className="flex gap-4" data-testid={`status-step-${k}`} data-state={done ? "done" : now ? "current" : "todo"}>
                  <Icon className={`h-6 w-6 shrink-0 ${done ? "text-emerald-600" : now ? "text-amber-500" : "text-slate-300"}`} />
                  <div><p className={`font-semibold ${i > idx ? "text-slate-400" : ""}`}>{title}</p><p className="text-sm text-slate-500">{text}</p></div>
                </li>
              );
            })}
          </ol>
          {d.progress === "ready" && <Button asChild data-testid="status-open-portal" className="w-full h-11 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white"><Link to="/portal/login">Open my client portal<ArrowRight className="h-4 w-4 ml-2" /></Link></Button>}
          {d.deposit && d.deposit.status !== "paid" && d.progress !== "ready" && (
            <div className="rounded-xl border border-slate-200 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="status-deposit">
              <p className="text-sm">Book your spot with a <span className="font-semibold">{money(d.deposit.amount, d.deposit.currency)}</span> deposit.</p>
              <Button asChild size="sm" className="rounded-full bg-[#ffc439] hover:bg-[#f2b92f] text-[#003087] font-bold"><Link to={`/deposit/${d.deposit.token}`} data-testid="status-pay-deposit">Pay deposit</Link></Button>
            </div>
          )}
          {d.deposit?.status === "paid" && <p className="text-sm text-emerald-700" data-testid="status-deposit-paid">Deposit of {money(d.deposit.amount, d.deposit.currency)} received. Thank you!</p>}
        </div>
      </div>
    </div>
  );
}
